import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    RadioGroup,
    Radio,
    FormControlLabel,
    Alert,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import orderService from '../../../services/orderService';
import returnService from '../../../services/returnService';
import productService from '../../../services/productService';

const ReturnForm = () => {
    const navigate = useNavigate();
    const { orderId } = useParams();
    const { t } = useTranslation();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [type, setType] = useState('RETURN');
    const [reason, setReason] = useState('DEFECTIVE');
    const [customerNote, setCustomerNote] = useState('');
    // selections[orderItemId] = { checked, quantity, exchangeVariantId }
    const [selections, setSelections] = useState({});
    // exchangeOptions[orderItemId] = [variants of the same product]
    const [exchangeOptions, setExchangeOptions] = useState({});

    useEffect(() => {
        fetchOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const fetchOrder = async () => {
        setInitialLoading(true);
        try {
            const data = await orderService.getOrderById(orderId);
            setOrder(data);
            const initial = {};
            (data.items || []).forEach((item) => {
                initial[item.id] = { checked: false, quantity: item.quantity, exchangeVariantId: '' };
            });
            setSelections(initial);
        } catch (err) {
            console.error('Failed to load order:', err);
            toast.error(t('common.loadError'));
        } finally {
            setInitialLoading(false);
        }
    };

    const toggleItem = (item, checked) => {
        setSelections((prev) => ({ ...prev, [item.id]: { ...prev[item.id], checked } }));
        if (checked && type === 'EXCHANGE' && !exchangeOptions[item.id]) {
            fetchExchangeOptions(item);
        }
    };

    const fetchExchangeOptions = async (item) => {
        try {
            // OrderItemDTO only carries variantId — resolve its product first.
            const variant = await productService.getProductVariantById(item.variantId);
            const variants = await productService.getProductVariants(variant.productId);
            setExchangeOptions((prev) => ({ ...prev, [item.id]: variants || [] }));
        } catch (err) {
            console.error('Failed to fetch exchange options:', err);
        }
    };

    const updateQuantity = (itemId, quantity) => {
        setSelections((prev) => ({ ...prev, [itemId]: { ...prev[itemId], quantity } }));
    };

    const updateExchangeVariant = (itemId, exchangeVariantId) => {
        setSelections((prev) => ({ ...prev, [itemId]: { ...prev[itemId], exchangeVariantId } }));
    };

    const handleTypeChange = (newType) => {
        setType(newType);
        if (newType === 'EXCHANGE') {
            Object.entries(selections).forEach(([itemId, sel]) => {
                if (sel.checked && !exchangeOptions[itemId]) {
                    const item = order.items.find((i) => i.id === itemId);
                    if (item) fetchExchangeOptions(item);
                }
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const items = Object.entries(selections)
            .filter(([, sel]) => sel.checked)
            .map(([orderItemId, sel]) => ({
                orderItemId,
                quantityReturned: parseInt(sel.quantity, 10),
                exchangeVariantId: type === 'EXCHANGE' ? (sel.exchangeVariantId || null) : null,
            }));

        if (items.length === 0) {
            toast.error(t('returns.selectItemError'));
            return;
        }
        if (type === 'EXCHANGE' && items.some((i) => !i.exchangeVariantId)) {
            toast.error(t('returns.selectExchangeVariantError'));
            return;
        }

        setLoading(true);
        try {
            await returnService.createReturn({
                orderId,
                type,
                reason,
                customerNote: customerNote || null,
                items,
            });
            toast.success(t('returns.createSuccess'));
            navigate('/returns');
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <Box sx={{ p: 3, textAlign: 'center' }}><Typography>{t('common.loading')}</Typography></Box>;
    }
    if (!order) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{t('orders.fetchError')}</Alert></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/orders/${orderId}`)}>
                    {t('common.back')}
                </Button>
                <Typography variant="h4">{t('returns.createReturn')}</Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            {t('orders.order')}: <strong>{order.orderNumber}</strong>
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" sx={{ mb: 1 }}>{t('returns.type')}</Typography>
                                <RadioGroup row value={type} onChange={(e) => handleTypeChange(e.target.value)}>
                                    <FormControlLabel value="RETURN" control={<Radio />} label={t('returns.typeOptions.RETURN')} />
                                    <FormControlLabel value="EXCHANGE" control={<Radio />} label={t('returns.typeOptions.EXCHANGE')} />
                                </RadioGroup>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('returns.reason')}</InputLabel>
                                    <Select value={reason} label={t('returns.reason')} onChange={(e) => setReason(e.target.value)}>
                                        <MenuItem value="DEFECTIVE">{t('returns.reasonOptions.DEFECTIVE')}</MenuItem>
                                        <MenuItem value="WRONG_ITEM">{t('returns.reasonOptions.WRONG_ITEM')}</MenuItem>
                                        <MenuItem value="NOT_AS_DESCRIBED">{t('returns.reasonOptions.NOT_AS_DESCRIBED')}</MenuItem>
                                        <MenuItem value="OTHER">{t('returns.reasonOptions.OTHER')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label={t('returns.customerNote')}
                                    value={customerNote}
                                    onChange={(e) => setCustomerNote(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>{t('returns.articlesToReturn')}</Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>{t('stockMovements.product')}</TableCell>
                                        <TableCell>{t('stockMovements.variant')}</TableCell>
                                        <TableCell align="right">{t('common.quantity')}</TableCell>
                                        {type === 'EXCHANGE' && <TableCell>{t('returns.exchangeFor')}</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(order.items || []).map((item) => {
                                        const sel = selections[item.id] || {};
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={Boolean(sel.checked)}
                                                        onChange={(e) => toggleItem(item, e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell>{item.variantName}</TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={sel.quantity ?? item.quantity}
                                                        onChange={(e) => updateQuantity(item.id, e.target.value)}
                                                        disabled={!sel.checked}
                                                        inputProps={{ min: 1, max: item.quantity, style: { width: 60, textAlign: 'right' } }}
                                                    />
                                                </TableCell>
                                                {type === 'EXCHANGE' && (
                                                    <TableCell>
                                                        <FormControl size="small" sx={{ minWidth: 180 }} disabled={!sel.checked}>
                                                            <Select
                                                                value={sel.exchangeVariantId || ''}
                                                                displayEmpty
                                                                onChange={(e) => updateExchangeVariant(item.id, e.target.value)}
                                                            >
                                                                <MenuItem value=""><em>{t('purchaseOrders_ext.selectVariant')}</em></MenuItem>
                                                                {(exchangeOptions[item.id] || [])
                                                                    .filter((v) => v.id !== item.variantId)
                                                                    .map((v) => (
                                                                        <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                                                                    ))}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                        {loading ? t('common.save') + '...' : t('returns.createReturn')}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`/orders/${orderId}`)}>
                        {t('common.cancel')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default ReturnForm;
